import util from 'util'
import React, { useCallback, useEffect, useMemo } from 'react'

import { ExitToApp, RecentActors, Settings, SupervisorAccount } from '@material-ui/icons'
import { CircularProgress, Switch, SwitchLabel, Typography } from '@barracuda-internal/bds-core'
import { ProfileMenu, BaseOption, ProductSwitcher, MenuItem, Box } from '@barracuda-internal/bds-core-v2'
import { OpenInNew } from '@barracuda-internal/bds-core-v2/dist/Icons/Core'
import { Apps, Earth as EarthIcon } from '@barracuda-internal/bds-core/dist/Icons/Core'
import { EmailLogCommandType } from '@barracuda-internal/converged-email-log'

import routesConfig, { UiRoute } from 'lib/routesConfig'
import { useAppSelector, useAppDispatch } from 'redux/toolkit/hooks'

import { appTypeRelatedPaths } from 'config/uiPaths'
import { AppTypes } from 'types/AppTypes'
import { useOverviewSubmenuItemsLogic } from 'components/libs/layout/navbar/submenuItems/useOverviewSubmenuItemsLogic'
import { useInboundSettingsSubmenuItemsLogic } from 'components/libs/layout/navbar/submenuItems/useInboundSettingsSubmenuItemsLogic'
import { useOutboundSettingsSubmenuItemsLogic } from 'components/libs/layout/navbar/submenuItems/useOutboundSettingsSubmenuItemsLogic'
import { useUsersSubmenuItemsLogic } from 'components/libs/layout/navbar/submenuItems/useUsersSubmenuItemsLogic'
import { useReportsSubmenuItemsLogic } from 'components/libs/layout/navbar/submenuItems/useReportsSubmenuItemsLogic'
import { useSettingsLogic } from 'components/libs/layout/navbar/submenuItems/useSettingsSubmenuItemsLogic'
import { useLogoutMenuLogic } from 'components/libs/layout/navbar/useLogoutMenuLogic'
import { SubMenuItem } from 'components/libs/layout/navbar/sidebarTypes'
import { useFormatMessage } from 'lib/localization'
import { ActivePath, closeSnackBar, setActivePath } from 'redux/features/app/appSlice'
import useLogout from 'lib/useLogout'
import useUserRights, { UserRights } from 'components/libs/userRights/useUserRights'
import { getSessionCookie } from 'lib/cookies'
import appConfig from 'config/appConfig'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'
import styles from 'components/libs/layout/navbar/sidebarStyles'
import { useUiSwitcherRights } from 'components/libs/userRights/libs/useUiSwitcherRights'
import { getPamProductData, EGD_PRODUCT_ID, IP_PRODUCT_ID, IR_PRODUCT_ID } from 'redux/features/pam/pamSlice'
import { isPending, isSuccess } from 'redux/toolkit/api'
import { getIpirAccount } from 'redux/features/ipir/ipirSlice'
import { trackEventInAllServices, TRACKING_EVENTS } from 'lib/monitoring/monitoringService'
import { GlobalNavigation, HOME_BUTTON_BARRACUDA } from 'redux/features/pam/pamApiThunks'
import logger from 'lib/logger'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import { useDomainPolicyAccess } from 'lib/hooks/useDomainPolicyAccess'
import { getAvatarInitials } from 'lib/avatarInitials'
import { useEmailLogBridge } from '../../../../EmailLogBridge'

const BASE_I18N_SNACKBAR = 'ess.snackbar'
const BASE_I18N_KEY = 'ess.sidemenu'
const BASE_I18N_COMMON = 'ess_language.language'

export type SideMenuItem = UiRoute

const MAIN_MENU_ITEMS = [
  // admin paths
  routesConfig.EMAIL_LOG,
  routesConfig.EMAIL_POLICIES,
  routesConfig.OVERVIEW,
  routesConfig.DOMAINS,
  routesConfig.INBOUND_SETTINGS,
  routesConfig.OUTBOUND_SETTINGS,
  routesConfig.ATP_SETTINGS,
  routesConfig.USERS,
  routesConfig.REPORT_LIST,
  routesConfig.ACCOUNT_MANAGEMENT,

  // enduser paths
  routesConfig.MESSAGE_LOG,
  routesConfig.SETTINGS,

  // common paths
  routesConfig.SUPPORT
]

export interface SideMenuLogic {
  sideMenuItems: SideMenuItem[]
  onSelectListItem: (selectedRouteId: string) => void
  subMenuItems: { [key: string]: SubMenuItem[] }
  isSideBarVisible: boolean
  isUsedOnetimePasscode: boolean
  isBlockedApp: boolean
  handleCloseSnackBar: (event?: React.SyntheticEvent, reason?: string) => void
  snackbarOpen: boolean
  snackbarSeverity: string
  snackBarMessage: string
  options: BaseOption[]
  isLanguageMenuDialogOpen: boolean
  onCloseLanguageMenu: () => void
  activePath: ActivePath
  homeButtonOptions?: GlobalNavigation
}

export const useSideBarLogic = (): SideMenuLogic => {
  const emailLogBridge = useEmailLogBridge()

  const classes = styles()
  const {
    appType,
    userName,
    userEmail,
    isImpersonatedUser,
    isValidatedSession,
    snackBar,
    isUsedOnetimePasscode,
    isBlockedApp,
    activePath,
    isImpersonated,
    serial,
    accountId,
    essAccountId,
    productData,
    productDataLoading,
    productDataSuccess,
    ipirAccountSuccess,
    ipirAccount,
    featureFlags,
    isDemoUser,
    pdDomainId
  } = useAppSelector(_store => ({
    appType: _store.app.appType,
    userName: _store.auth.accessTokenObject?.userInfo?.name,
    userEmail: _store.auth.accessTokenObject?.userId,
    isImpersonatedUser: !!_store.auth.accessTokenObject?.isImpersonated,
    isValidatedSession: _store.auth.isValidatedSession,
    snackBar: _store.app.snackBar,
    isUsedOnetimePasscode: !!_store.auth.accessTokenObject?.isUsedOnetimePasscode,
    isBlockedApp: _store.app.isBlocked,
    activePath: _store.app.activePath,
    isImpersonated: !!_store.auth.accessTokenObject?.isImpersonated,
    serial: _store.auth.accessTokenObject?.serial,
    accountId: _store.auth.accessTokenObject?.bccAccountId,
    essAccountId: _store.auth.accessTokenObject?.accountId,
    productData: _store.pam.productData,
    productDataLoading: isPending(_store.pam.api.getProductData),
    productDataSuccess: isSuccess(_store.pam.api.getProductData),
    ipirAccountSuccess: isSuccess(_store.ipir.api.getIpirAccount),
    ipirAccount: _store.ipir.account,
    featureFlags: _store.auth.accessTokenObject?.featureFlags || {},
    isDemoUser: _store.auth.accessTokenObject?.isGuestUser || false,
    pdDomainId: _store.auth.accessTokenObject?.pdDomainId
  }))
  const [overviewSubMenuItems] = useOverviewSubmenuItemsLogic()
  const [InboundSettingswSubMenuItems] = useInboundSettingsSubmenuItemsLogic()
  const [outboundSettingsListItems] = useOutboundSettingsSubmenuItemsLogic()
  const [usersSubMenuItems] = useUsersSubmenuItemsLogic()
  const [reportsSubMenuItems] = useReportsSubmenuItemsLogic()
  const [settingsSubMenuItems] = useSettingsLogic()
  const dispatch = useAppDispatch()
  const snackFormatMessage = useFormatMessage(BASE_I18N_SNACKBAR)
  const [logout, logoutImpersonatedEnduser] = useLogout()
  const { userHasRight } = useUserRights()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const commonFormatMessage = useFormatMessage(BASE_I18N_COMMON)
  const isReadonly = useIsReadonly()
  const { canAccessDomainPolicies, canViewDomainMessages } = useDomainPolicyAccess(Number(pdDomainId) || 0)

  const onLogout = useCallback(() => {
    if (isImpersonated) {
      logoutImpersonatedEnduser()
      return
    }
    logger.info('Manual logout triggered from side bar')
    logout(true)
  }, [isImpersonated, logoutImpersonatedEnduser, logout])
  const {
    handleLogout,
    onOpenLanguageMenu,
    preferredLanguage,
    isLicenseComplianceOn,
    onOpenReviewLicense,
    isLanguageMenuDialogOpen,
    onCloseLanguageMenu,
    onSwitchToOldUi
  } = useLogoutMenuLogic({
    onLogout,
    handleClose: () => false
  })
  const isReportingOn = isMyFeatureOn(FEATURES.EGD_React_Reporting_Rollout)
  const { canViewPhaseTwoFeature } = useUiSwitcherRights()

  const onOpenBccProfile = useCallback(() => {
    window.open(`${appConfig.BCC_PATH}/account/profile`, '_blank')
  }, [])

  const onOpenBccAdmin = useCallback(() => {
    window.open(`${appConfig.BCC_PATH}/account`, '_blank')
  }, [])

  useEffect(() => {
    if (appConfig.APP.IS_ADMIN) {
      dispatch(getPamProductData())
    }
  }, [dispatch])

  useEffect(() => {
    if (appConfig.APP.IS_ADMIN && productDataSuccess && appConfig.INCOMPLETE_IPIR_SETUP_BANNER) {
      let hasIp = false
      let hasIr = false
      let hasEgd = false
      productData?.data.subscriptionGroups.forEach(group => {
        group?.subscriptions.forEach(subscription => {
          if (subscription.product.id === EGD_PRODUCT_ID && subscription.status === 'ACTIVE' && !subscription.isTrial) {
            hasEgd = true
          }
          if (subscription.product.id === IP_PRODUCT_ID && subscription.status === 'ACTIVE' && !subscription.isTrial) {
            hasIp = true
          }
          if (subscription.product.id === IR_PRODUCT_ID && subscription.status === 'ACTIVE' && !subscription.isTrial) {
            hasIr = true
          }
        })
      })

      // If they have all three products we need to check ipir tokens
      if (hasIp && hasIr && hasEgd) {
        dispatch(getIpirAccount())
      }
    }
  }, [dispatch, productDataSuccess, productData])

  useEffect(() => {
    if (!appConfig.APP.IS_ADMIN) {
      return
    }

    // If we don't have tokens that are not ETS that means the account is not setup for IP/IR so we display intercom
    if (ipirAccountSuccess && !ipirAccount?.accessTokens.filter(token => !token.products.includes('ets')).length) {
      trackEventInAllServices(TRACKING_EVENTS.ADMIN.INCOMPLETE_IP_OR_IR)
    }
  }, [ipirAccountSuccess, ipirAccount])

  const onClickCookiePreferences = useCallback(() => {
    const el = document.getElementById('ot-sdk-btn')
    if (el) {
      el.click()
    }
  }, [])

  const showLogoutMenu = useMemo(() => {
    if (appConfig.APP.IS_WIZARD) {
      return false
    }

    const isAdminMessageDetails =
      userHasRight(UserRights.VIEW_ADMIN_MESSAGES_DETAILS) && activePath.id === routesConfig.MESSAGE_DETAIL.id
    return !!getSessionCookie() && !isAdminMessageDetails
  }, [userHasRight, activePath])

  const emailLogRouteIds = useMemo(() => {
    const routeIds: string[] = []
    if (routesConfig.EMAIL_LOG?.id) {
      routeIds.push(routesConfig.EMAIL_LOG.id)
    }
    if (routesConfig.EMAIL_POLICIES?.id) {
      routeIds.push(routesConfig.EMAIL_POLICIES.id)
    }
    return routeIds
  }, [])

  const isEmailLogRoute = useCallback((uiRoute: UiRoute) => emailLogRouteIds.includes(uiRoute.id), [emailLogRouteIds])

  const isEmailLogPathActive = useMemo(() => {
    if (!activePath.id) {
      return false
    }
    return emailLogRouteIds.includes(activePath.id)
  }, [activePath.id, emailLogRouteIds])

  const availableAppListItems = useMemo(() => {
    const menuItemFilter = (uiRoute: UiRoute) => {
      if (!appTypeRelatedPaths[appType].includes(uiRoute?.id)) {
        return false
      }
      if (!isEmailLogPathActive && isEmailLogRoute(uiRoute)) {
        // Do not display EmailLog icons in EGD sidebar
        return false
      }
      if (isEmailLogPathActive && !isEmailLogRoute(uiRoute)) {
        // Do not display EGD icons in EmailLog sidebar
        return false
      }
      if (
        routesConfig.EMAIL_POLICIES &&
        !featureFlags.FEATURE_FLAG_EMAIL_POLICIES_ENABLED &&
        uiRoute?.id === routesConfig.EMAIL_POLICIES.id
      ) {
        // Do not display EmailPolicies link when the feature flag is OFF
        return false
      }
      if (pdDomainId && uiRoute?.id === routesConfig.MESSAGE_LOG?.id) {
        // Only show Message Log sidebar item if user has domain_level_messages R access
        return canViewDomainMessages
      }
      if (pdDomainId && uiRoute?.id === routesConfig.INBOUND_SETTINGS?.id) {
        // Hide Inbound Settings if no domain_level_policies access
        return canAccessDomainPolicies
      }
      if (pdDomainId && uiRoute?.id === routesConfig.OUTBOUND_SETTINGS?.id) {
        // Hide Outbound Settings if no domain_level_policies access
        return canAccessDomainPolicies
      }
      if (uiRoute.id === routesConfig.REPORT_LIST?.id && !isReportingOn) {
        return false
      }
      if (uiRoute.id === routesConfig.SUPPORT?.id && appType === AppTypes.cplAccount) {
        // Classic hides the Support tab for CPL; the route stays reachable for the help links
        return false
      }
      return true
    }
    return MAIN_MENU_ITEMS.filter(menuItemFilter)
  }, [
    appType,
    isEmailLogPathActive,
    isEmailLogRoute,
    featureFlags.FEATURE_FLAG_EMAIL_POLICIES_ENABLED,
    pdDomainId,
    isReportingOn,
    canAccessDomainPolicies,
    canViewDomainMessages
  ])

  const initials = useMemo(() => getAvatarInitials({ accountName: userName, email: userEmail }), [userEmail, userName])

  const onSelectListItem = useCallback(
    (selectedRouteId: string) => {
      const selectedRoute = Object.values(routesConfig).find((uiRoute: UiRoute) => uiRoute.path === selectedRouteId)

      //
      // INTERNAL EMAIL LOG NAVIGATION
      // When the email log is active, this is handled
      // with email log commands, not EGD routing
      //
      if (selectedRoute && isEmailLogPathActive) {
        const emailLogApp = emailLogBridge.getApp()
        if (!emailLogApp) {
          return
        }

        let nextPath // CML app to navigate to
        if (selectedRoute?.id === routesConfig.EMAIL_LOG?.id) {
          nextPath = '/'
        }
        if (selectedRoute?.id === routesConfig.EMAIL_POLICIES?.id) {
          nextPath = '/email-policies'
        }

        if (nextPath) {
          emailLogApp.dispatchCommand({
            type: EmailLogCommandType.NAVIGATE,
            payload: {
              path: nextPath
            }
          })
          dispatch(
            setActivePath({
              id: selectedRoute.id || '',
              url: `/${appType}/${selectedRoute.url()}`,
              legacyPath: selectedRoute.legacyPath,
              urlParams: '',
              params: {},
              metadata: selectedRoute.metadata || {},
              isNavbarVisible: true,
              isPublicRoute: false
            })
          )
        }
        return
      }

      //
      // EGD routing
      //
      if (selectedRoute && selectedRoute.path !== activePath.id) {
        selectedRoute.goto()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePath, isEmailLogPathActive, emailLogBridge, dispatch]
  )

  const subMenuItems = useMemo(
    () =>
      MAIN_MENU_ITEMS.reduce((prev, uiRoute) => {
        if (!uiRoute) {
          return prev
        }

        switch (uiRoute.id) {
          case routesConfig.OVERVIEW.id:
            return {
              ...prev,
              [uiRoute.id]: overviewSubMenuItems
            }
          case routesConfig.INBOUND_SETTINGS.id:
            return {
              ...prev,
              [uiRoute.id]: InboundSettingswSubMenuItems
            }
          case routesConfig.OUTBOUND_SETTINGS.id:
            return {
              ...prev,
              [uiRoute.id]: outboundSettingsListItems
            }
          case routesConfig.USERS.id:
            return {
              ...prev,
              [uiRoute.id]: usersSubMenuItems
            }
          case appConfig.APP.IS_ADMIN && routesConfig.REPORT_LIST.id:
            return {
              ...prev,
              [uiRoute.id]: reportsSubMenuItems
            }
          case appConfig.APP.IS_ENDUSER && routesConfig.SETTINGS.id:
            return {
              ...prev,
              [uiRoute.id]: settingsSubMenuItems
            }
          default:
            return prev
        }
      }, {}),
    [
      InboundSettingswSubMenuItems,
      outboundSettingsListItems,
      overviewSubMenuItems,
      reportsSubMenuItems,
      settingsSubMenuItems,
      usersSubMenuItems
    ]
  )

  const snackBarMessage = useMemo(() => {
    if (!snackBar.message) {
      return ''
    }

    let message
    if (snackBar.message.startsWith('@literal:')) {
      message = snackBar.message.substring(9)
    } else {
      message = snackFormatMessage(snackBar.message)
    }

    if (snackBar.params) {
      return util.format(message, ...snackBar.params)
    }
    return message
  }, [snackBar, snackFormatMessage])

  const handleCloseSnackBar = useCallback(
    (_?: React.SyntheticEvent, reason?: string) => {
      if (reason === 'clickaway') {
        return
      }

      dispatch(closeSnackBar())
    },
    [dispatch]
  )

  const options = useMemo(() => {
    const sidebarOptions = []
    const menuItems = []
    if (appConfig.APP.IS_ADMIN) {
      if (activePath.id !== routesConfig.EMAIL_LOG?.id) {
        const settingsMenuItems = []
        settingsMenuItems.push(
          <MenuItem key="bcc-admin" onClick={onOpenBccAdmin}>
            {formatMessage('bcc_admin')}
            <OpenInNew sx={{ fill: 'rgb(175, 175, 174)', marginLeft: 'auto' }} />
          </MenuItem>
        )
        if (canViewPhaseTwoFeature) {
          settingsMenuItems.push(
            <SwitchLabel
              key="opt-in"
              className={classes.optOut}
              control={<Switch size="small" checked onChange={onSwitchToOldUi} color="primary" />}
              label={
                <Typography className={classes.toggleLabel} variant="body1">
                  {formatMessage('new_interface')}
                </Typography>
              }
            />
          )
        }
        sidebarOptions.push({
          name: formatMessage('settings'),
          icon: <Settings />,
          menu: (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              {settingsMenuItems}
            </Box>
          )
        })
      }
      if (productDataLoading) {
        sidebarOptions.push({
          name: '',
          disabled: true,
          icon: <CircularProgress />,
          menu: null
        })
      } else if (productData && productData.data) {
        sidebarOptions.push({
          name: formatMessage('your_apps'),
          icon: <Apps />,
          menu: (
            <ProductSwitcher
              currentProductId={activePath.id !== routesConfig.EMAIL_LOG?.id ? EGD_PRODUCT_ID : undefined}
              subscriptionGroups={productData.data.subscriptionGroups}
            />
          )
        })
      }
      menuItems.push({
        name: formatMessage('manage_profile'),
        onClick: onOpenBccProfile,
        icon: <SupervisorAccount />,
        endIcon: <OpenInNew />
      })
      if (appConfig.APP.IS_ADMIN && isLicenseComplianceOn) {
        menuItems.push({
          name: formatMessage('review_licenses'),
          onClick: onOpenReviewLicense,
          icon: <RecentActors />
        })
      }
    }

    if (showLogoutMenu) {
      const cookiePreferencesItem = appConfig.ONETRUST_ID
        ? [
            {
              name: formatMessage('cookie_preferences'),
              onClick: onClickCookiePreferences,
              icon: ' '
            }
          ]
        : []
      menuItems.push(
        {
          name: formatMessage('language_selector', { language: commonFormatMessage(preferredLanguage) }),
          onClick: onOpenLanguageMenu,
          icon: <EarthIcon />
        },
        ...cookiePreferencesItem,
        {
          name: formatMessage('logout'),
          onClick: handleLogout,
          icon: <ExitToApp />
        }
      )
    }

    sidebarOptions.push({
      name: formatMessage('profile'),
      initials,
      menu: (
        <ProfileMenu
          initials={initials}
          name={userName}
          email={userEmail}
          menuItems={menuItems}
          footer={{
            customItems: [
              {
                label: formatMessage('account_id'),
                value: `${accountId} (${essAccountId})`
              }
            ],
            serial: serial || ''
          }}
        />
      )
    })
    return sidebarOptions
  }, [
    showLogoutMenu,
    formatMessage,
    initials,
    userName,
    userEmail,
    onOpenBccAdmin,
    canViewPhaseTwoFeature,
    productData,
    productDataLoading,
    classes.optOut,
    classes.toggleLabel,
    onSwitchToOldUi,
    onOpenBccProfile,
    isLicenseComplianceOn,
    onOpenReviewLicense,
    commonFormatMessage,
    preferredLanguage,
    onOpenLanguageMenu,
    onClickCookiePreferences,
    handleLogout,
    activePath,
    serial,
    accountId,
    essAccountId
  ])

  return useMemo(
    () => ({
      sideMenuItems: availableAppListItems,
      onSelectListItem,
      subMenuItems,
      isSideBarVisible: isValidatedSession && (!!userEmail || isImpersonatedUser),
      isUsedOnetimePasscode,
      isBlockedApp,
      handleCloseSnackBar,
      snackbarOpen: snackBar.open,
      snackbarSeverity: snackBar.severity,
      snackBarMessage,
      options,
      isLanguageMenuDialogOpen,
      onCloseLanguageMenu,
      activePath,
      homeButtonOptions:
        isReadonly && !isDemoUser
          ? undefined
          : productData?.data.globalNavigation?.find(option => option.id === HOME_BUTTON_BARRACUDA)
    }),
    [
      availableAppListItems,
      onSelectListItem,
      subMenuItems,
      isValidatedSession,
      isImpersonatedUser,
      isUsedOnetimePasscode,
      isBlockedApp,
      handleCloseSnackBar,
      snackBar.open,
      snackBar.severity,
      snackBarMessage,
      options,
      isLanguageMenuDialogOpen,
      userEmail,
      onCloseLanguageMenu,
      activePath,
      productData,
      isReadonly,
      isDemoUser
    ]
  )
}
